// @flow

/**
 * Module dependencies.
 */

import { Col, Container, Row } from 'client/components/core/grid';
import { branch } from 'core/utils';
import { breakpoints, colors, defaultTransition, fixedHeight, units, zIndex } from 'client/styles';
import { compose, defaultProps, mapProps, withState } from 'recompose';
import { isPhone, setBodyOverflow } from 'client/core/utils';
import { position } from 'polished';
import { withRouter } from 'react-router';
import Layout from 'client/components/core/layout';
import Logo from 'client/components/core/logo';
import MediaQuery from 'react-responsive';
import MenuMain from 'client/components/navbar/menu-main';
import MenuMobile from 'client/components/navbar/menu-mobile';
import MenuRight from 'client/components/navbar/menu-right';
import Navicon from 'client/components/navbar/navicon';
import React, { Component } from 'react';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  closeMobileMenu: Function,
  footerLinks: Array<Object>,
  isOpen: boolean,
  isSticky: boolean,
  links: Array<Object>,
  location: Object,
  locationPath: string,
  loginLabel: string,
  toggleMobileMenu: Function,
  toogle: Function
};

/**
 * `NavbarStyled` component.
 */

const NavbarStyled = styled.header`
  ${position('0', '0', null, '0')};

  height: ${isPhone() ? fixedHeight.navbarMobile : fixedHeight.navbar};
  transition: transform ${defaultTransition};
  width: 100%;
  z-index: ${zIndex.navbar};

  ${({ sticky }) => `
    background-color: ${sticky ? colors.shadesOfSand2.pampasDarker : 'transparent'};
    position: ${sticky ? 'fixed' : 'absolute'};
    top: ${sticky ? '-80px' : '0'};
    transform: translateY(${sticky ? fixedHeight.navbar : '0'});
  `}
`;

/**
 * `Navbar` component.
 */

class Navbar extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * Handle esc key.
   */

  handleEscKey = (event: any) => {
    const { closeMobileMenu } = this.props;

    if (event.which === 27) {
      closeMobileMenu();
    }
  }

  /**
   * Disabled mobile menu.
   */

  disabledMobileMenu = () => {
    const { innerWidth } = window;
    const { isOpen, closeMobileMenu } = this.props;

    if (isOpen && innerWidth > breakpoints.md) {
      closeMobileMenu();
    }
  }

  /**
   * Is logo visible.
   */

  isLogoVisible = () => {
    const { isSticky, location } = this.props;
    const { pathname } = location;
    const isHome = pathname === '/en' || pathname === '/pt';

    return isHome && !isSticky;
  }

  /**
   * Render logo.
   */

  renderLogo(): React$Element<*> {
    const { isOpen, isSticky } = this.props;
    const logoColor = isSticky || isOpen ? colors.brandSecondary : colors.white;

    return (
      <Layout marginTop={units(2.25)}>
        <Logo color={logoColor} />
      </Layout>
    );
  }

  /**
   * Component will mount.
   */

  componentWillMount() {
    document.addEventListener('keydown', this.handleEscKey, false);
    window.addEventListener('resize', this.disabledMobileMenu, false);
  }

  /**
   * Component will unmount.
   */

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleEscKey, false);
    window.removeEventListener('resize', this.disabledMobileMenu, false);
  }

  /**
   * Component will receive props.
   */

  componentWillReceiveProps(nextProps: Props) {
    const { isOpen } = nextProps;

    if (isOpen === this.props.isOpen) {
      return;
    }

    setBodyOverflow(isOpen);
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const {
      footerLinks,
      isOpen,
      isSticky,
      links,
      locationPath,
      loginLabel,
      toggleMobileMenu
    } = this.props;

    return (
      <NavbarStyled sticky={isSticky}>
        <Layout
          position={'relative'}
          zIndex={1}
        >
          <Container>
            <Row>
              <Col
                md={2}
                sm={5}
                xs={4}
                xsOffset={1}
              >
                <MediaQuery maxWidth={breakpoints.md - 1}>
                  {this.renderLogo()}
                </MediaQuery>

                <MediaQuery minWidth={breakpoints.md}>
                  {branch(!this.isLogoVisible(), this.renderLogo())}
                </MediaQuery>
              </Col>

              <MediaQuery minWidth={breakpoints.md}>
                <Col md={6}>
                  <Layout textAlign={'center'}>
                    <MenuMain
                      fixed={isSticky || isOpen}
                      links={links}
                      locationPath={locationPath}
                    />
                  </Layout>
                </Col>
              </MediaQuery>

              <Col
                md={2}
                sm={5}
                xs={6}
              >
                <Layout
                  marginTop={units(isPhone() ? 2.5 : 3.25)}
                  textAlign={'right'}
                >
                  <MediaQuery minWidth={breakpoints.xs}>
                    <MenuRight
                      fixed={isSticky || isOpen}
                      locationPath={locationPath}
                      loginLabel={loginLabel}
                    />
                  </MediaQuery>

                  <MediaQuery maxWidth={breakpoints.md - 1}>
                    <Navicon
                      active={isOpen}
                      fixed={isSticky || isOpen}
                      toggle={toggleMobileMenu}
                    />
                  </MediaQuery>
                </Layout>
              </Col>
            </Row>
          </Container>
        </Layout>

        <MediaQuery maxWidth={breakpoints.md}>
          <MenuMobile
            footerLinks={footerLinks}
            isOpen={isOpen}
            loginLabel={loginLabel}
            menuLinks={links}
          />
        </MediaQuery>
      </NavbarStyled>
    );
  }

}

/**
 * Export `Navbar`.
 */

export default compose(
  defaultProps({
    isOpen: false
  }),
  withState('isOpen', 'toggle', false),
  mapProps(({ toggle, ...props }: Object): Props => ({
    ...props,
    closeMobileMenu: (): Function => toggle(false),
    toggleMobileMenu: (): Function => toggle((isOpen: boolean): boolean => !isOpen)
  })),
  withRouter
)(Navbar);



// WEBPACK FOOTER //
// ../src/client/components/navbar/index.js